import { IpaType, isStress } from "../phonemes/ipa";
import { en_US_Abbrevs } from "../transcribe/en_US-abbrev";
import { substituteNumbers } from "../transcribe/numberToEnglish";
import { wordsToIpa } from "../transcribe/wordsToIpa";
import { parseUnits } from "../units/parseUnits";
import { WordUnit } from "../units/wordUnit";

/** names of the standard lexical sets */
export type SetName =
  // diphthongs in US
  | 'FACE' | 'PRICE' | 'GOAT' | 'CHOICE' | 'MOUTH'
  // r-colored in US
  | 'START' | 'SQUARE' | 'NEAR' | 'NORTH' | 'FORCE' | 'CURE' | 'lettER' | 'NURSE'
  // single sound in US
  | 'PALM' | 'LOT' | 'THOUGHT' | 'CLOTH' | 'TRAP' | 'BATH' | 'DRESS' | 'FLEECE' | 'happY' | 'KIT' | 'GOOSE' | 'FOOT' | 'STRUT' | 'commA';

/** description of how good the guesses are when attempting to detect lexical sets */
export const SetSuccesses = [
  {
    chance: 'strong',
    sets: ['GOAT', 'START', 'SQUARE', 'NEAR', 'CURE', 'lettER', 'NURSE',
      'PALM', 'LOT', 'THOUGHT', 'CLOTH', 'BATH', 'FLEECE', 'GOOSE', 'STRUT', 'commA',],
    why: 'US & UK are different and unique'
  },
  {
    chance: 'good',
    sets: ['FACE', 'PRICE', 'CHOICE', 'MOUTH',],
    why: 'diphthongs, US & UK are the same'
  },
  {
    chance: 'weak',
    sets: ['TRAP', 'DRESS', 'happY', 'KIT', 'FOOT',],
    why: 'single phoneme, US & UK are the same'
  },
  {
    chance: 'special',
    sets: ['NORTH', 'FORCE'],
    why: "NORTH & FORCE are different, but US & UK don't distinguish them, default to NORTH"
  },
];

/**
 * detect each of the lexical sets that are present and replace the vowels with (##),
 * it infers them by comparing US & UK phonemes for the words, which works most of the time
 */
export function findLexicalSets(english: string): string {
  const units = parseUnits(english, en_US_Abbrevs, { punctuation: true, lowerCase: true });
  const substitution = units.flatMap(word => {
    const result = substituteNumbers(word.input);
    return Array.isArray(result) ? result.map(r => { return { ...word, input: r } }) : { ...word, result };
  });
  const withSets = substitution.map(word => findSetsForWord(word));
  return withSets.join(' ');
}

export function findSetsForWord(englishWord: WordUnit, useRPDictionary?: boolean, fallback?: (word: string) => string): string {
  const hardcoded = hardcodedSets[englishWord.input];
  if (hardcoded && typeof hardcoded === 'string') {
    return hardcoded;
  }

  // compare US to UK pronunciations to help figure out appropriate lexical set
  const [us] = wordsToIpa([englishWord], 'en_US');
  const [uk] = wordsToIpa([englishWord], 'en_UK');
  if (us === '' && uk === '') {
    // not in either dictionary, use fallback
    const [guess] = wordsToIpa([englishWord], undefined, fallback);
    return findSetsForIpa(guess, guess, useRPDictionary);
  }
  return findSetsForIpa(us, uk, useRPDictionary);
}

export function findSetsForIpa(us: IpaType, uk: IpaType, useRPDictionary?: boolean): string {
  const useUS = us !== '';
  const useUK = uk !== '';
  if (!useUS && !useUK) {
    return '';
  }
  if (!uk) {
    // uk dictionary is very incomplete, so fallback to us,
    // note that this may mean that we guess at a set that perhaps we shouldn't
    // because we don't really know if/how the uk version differs
    useRPDictionary = false;
    uk = us;
  } else if (!us) {
    useRPDictionary = true;
    us = uk;
  }

  let result = '';
  const max = useRPDictionary ? uk.length : us.length;
  for (let iUS = 0, iUK = 0; (useRPDictionary ? iUK : iUS) < max; iUS++, iUK++) {
    if (us[iUS] !== 'j' && !ignore(us[iUS]) && uk[iUK] === 'j' && uk[iUK - 1] !== undefined) {
      // special handling for cases like /ˈtun/ versus /tjˈuːn/
      if (useRPDictionary) {
        result += uk[iUK];
      }
      iUK++;
    }
    if (us[iUS] === 'ˈ' && us[iUS + 1] === 'h' && us[iUS + 2] === 'w' && uk[iUK] === 'w' && uk[iUK + 1] === 'ˈ') {
      // special handling for cases like /ˈhweɪ/ versus /wˈeɪ/
      if (!useRPDictionary) {
        result += us[iUS] + us[iUS + 1] + us[iUS + 2];
      } else {
        result += uk[iUK] + uk[iUK + 1];
      }
      iUS += 3;
      iUK += 2;
    }

    if (ignore(us[iUS])) {
      if (!useRPDictionary) {
        result += us[iUS];
      }
      iUS++;
    }
    if (ignore(uk[iUK])) {
      if (useRPDictionary && uk[iUK] !== zeroWidthJoiner) {
        result += uk[iUK];
      }
      iUK++;
    }
    const setCheck = checkForLexicalSets(useUS, us, iUS, useUK, uk, iUK);
    if (setCheck !== null) {
      const [set, use] = setCheck;
      result += use ? set.token : (useRPDictionary ? uk[iUK] : us[iUS]);
      iUS += set.us.length - 1;
      iUK += set.uk.length - 1;
      if (set.rhotic && uk[iUK + 1] === 'ɹ') {
        // a rhotic set followed by /ɹ/ should include the r in the set
        iUK++;
      }
    } else {
      result += useRPDictionary ? uk[iUK] : us[iUS];
    }
  }
  return result;
}

const zeroWidthJoiner = '\u200d';
function ignore(c: string): boolean {
  return isStress(c) || c === zeroWidthJoiner;
}

/**
 * can return null for no set found,
 * or the set it found & whether it should be used,
 * e.g., we may find {us:/ɔ/,uk:/ɔː/}, but it's followed by /ˈɹ/ so we should skip it,
 * which means we need to know to skip 1 us & 2 uk chars, so we still return the set
 */
function checkForLexicalSets(
  useUS: boolean, us: string, iUS: number, useUK: boolean, uk: string, iUK: number
): [LexicalSet, boolean] | null {
  for (const set of lexicalSets) {
    if ((!useUS || us.startsWith(set.us, iUS)) && (!useUK || uk.startsWith(set.uk, iUK))) {
      if (set.us.length === 1 && useUS && us[iUS + 1] === "ˈ" && us[iUS + 2] === "ɹ") {
        // special handling so we don't pick a single phoneme set when the vowel is followed by /ˈɹ/
        return [set, false];
      }
      if (!set.onlyEnd || iUS === us.length - 1) {  // skip onlyEnd rule if we're not at the end
        return [set, true];
      }
    }
  }
  return null;
}

interface LexicalSet {
  readonly token: string;
  readonly us: string;
  readonly uk: string;
  readonly name: SetName;
  readonly rhotic?: boolean;  /** UK only has /ɹ/ if trailing vowel */
  readonly onlyEnd?: boolean;
}

export const lexicalSets: LexicalSet[] = [
  { token: '( 0)', us: 'eɪ̯', uk: 'eɪ̯', name: 'FACE' },
  { token: '( 1)', us: 'aɪ̯', uk: 'aɪ̯', name: 'PRICE' },
  { token: '( 2)', us: 'oʊ̯', uk: 'əʊ̯', name: 'GOAT' },
  { token: '( 3)', us: 'ɔɪ̯', uk: 'ɔɪ̯', name: 'CHOICE' },
  { token: '( 4)', us: 'aʊ̯', uk: 'aʊ̯', name: 'MOUTH' },
  { token: '( 5)', us: 'ɑɹ', uk: 'ɑː', name: 'START', rhotic: true },
  { token: '( 6)', us: 'ɛɹ', uk: 'eə̯', name: 'SQUARE', rhotic: true },
  { token: '( 7)', us: 'ɪɹ', uk: 'iə̯', name: 'NEAR', rhotic: true },
  { token: '( 8)', us: 'ɔɹ', uk: 'ɔː', name: 'NORTH', rhotic: true },
  { token: '( 9)', us: 'ɔɹ', uk: 'ɔː', name: 'FORCE', rhotic: true },
  { token: '(10)', us: 'ʊɹ', uk: 'ɔː', name: 'CURE', rhotic: true },
  { token: '(11)', us: 'ɝ', uk: 'ɐ', name: 'lettER', rhotic: true, onlyEnd: true },
  { token: '(12)', us: 'ɝ', uk: 'ɜː', name: 'NURSE', rhotic: true },
  { token: '(13)', us: 'ɑ', uk: 'ɑː', name: 'PALM' },
  { token: '(14)', us: 'ɑ', uk: 'ɒ', name: 'LOT' },
  { token: '(15)', us: 'ɔ', uk: 'ɔː', name: 'THOUGHT' },
  { token: '(16)', us: 'ɔ', uk: 'ɒ', name: 'CLOTH' },
  { token: '(17)', us: 'æ', uk: 'æ', name: 'TRAP' },
  { token: '(18)', us: 'æ', uk: 'ɑː', name: 'BATH' },
  { token: '(19)', us: 'ɛ', uk: 'ɛ', name: 'DRESS' },
  { token: '(20)', us: 'i', uk: 'iː', name: 'FLEECE' },
  { token: '(21)', us: 'i', uk: 'i', name: 'happY', onlyEnd: true },
  { token: '(22)', us: 'ɪ', uk: 'ɪ', name: 'KIT' },
  { token: '(23)', us: 'u', uk: 'uː', name: 'GOOSE' },
  { token: '(24)', us: 'ʊ', uk: 'ʊ', name: 'FOOT' },
  { token: '(25)', us: 'ə', uk: 'ʌ', name: 'STRUT' },
  { token: '(26)', us: 'ə', uk: 'ɐ', name: 'commA', onlyEnd: true },
];

export const lexicalSetsAlphaOrdered: SetName[] = [
  'BATH', 'CHOICE', 'CLOTH', 'commA', 'CURE',
  'DRESS', 'FACE', 'FLEECE', 'FOOT', 'FORCE',
  'GOAT', 'GOOSE', 'happY', 'KIT', 'lettER',
  'LOT', 'MOUTH', 'NEAR', 'NORTH', 'NURSE',
  'PALM', 'PRICE', 'SQUARE', 'START', 'STRUT',
  'THOUGHT', 'TRAP',
];
// from least variation across accents to most (missing unstressed sets lettER, happY, commA)
export const lexicalSetsVariationOrdered: SetName[] = [
  'KIT', 'DRESS', 'TRAP', 'LOT', 'STRUT', 'FOOT',
  'BATH', 'CLOTH', 'NURSE', 'FLEECE', 'FACE', 'PALM',
  'THOUGHT', 'GOAT', 'GOOSE', 'PRICE', 'CHOICE', 'MOUTH',
  'NEAR', 'SQUARE', 'START', 'NORTH', 'FORCE', 'CURE',
]
// the ordering at https://en.wikipedia.org/wiki/International_Phonetic_Alphabet_chart_for_English_dialects
export const lexicalSetsWikiOrdered: SetName[] = [
  'TRAP', 'BATH', 'PALM', 'LOT', 'CLOTH', 'THOUGHT', 'commA',
  'KIT', 'happY', 'FLEECE', 'FACE', 'DRESS', 'STRUT', 'FOOT', 'GOOSE',
  'PRICE', 'CHOICE', 'GOAT', 'MOUTH',
  'START', 'NEAR', 'SQUARE', 'NURSE',
  'lettER', 'NORTH', 'FORCE', 'CURE',
];

// words we know are in a particular lexical set, but don't get detected properly
const hardcodedSets: Record<string, string> = {
  // UK dictionary differs from other items in ( 1)
  "arrive": "ɝˈ( 1)v",
  // UK dictionary differs from other items in ( 3)
  "choice": "ˈt͡ʃ( 3)s",
  // UK dictionary differs from other items in ( 6)
  "where": "ˈhw( 6)",
  // UK dictionary differs from other items in ( 7)
  "serum": "ˈs( 7)əm",
  // US dictionary differs from other items in ( 7)
  "interfere": "ˌ(22)nt(12)ˈf( 7)",
  // US dictionary differs from other items in (10)
  "poor": "ˈp(10)",
  // UK dictionary differs from other items in (10)
  "mural": "ˈmj(10)əɫ",
  "curious": "ˈkj(10)iəs",
  // US: ˈstupɝ, UK: stjˈuːpɐ
  "stupor": "ˈstup(11)",
  // US: ˈfɪɡjɝ, UK: fˈɪɡɐ
  "figure": "ˈf(22)ɡ(11)",
  // should be TRAP
  "piano": "piˈ(17)n( 2)",
  // UK dictionary doesn't have these words
  "committee": "kəˈmɪt(21)",
  "chelsea": "ˈt͡ʃɛɫs(21)",
  // UK dictionary has different pronunciation or is missing word
  "new": "ˈn(23)",
  // looks like NEAR, but should be KIT
  "syria": "ˈs(22)ɹ(20)(26)",
  // UK puts these in CLOTH (16), but should be THOUGHT (15)
  halt: "ˈh(15)ɫt",
  salt: "ˈs(15)ɫt",
  malt: "ˈm(15)ɫt",
  false: "ˈf(15)ɫs",
  alter: "ˈ(15)ɫt(11)",
  also: "ˈ(15)ɫs( 2)",
  walrus: "ˈw(15)ɫɹəs",
  fault: "ˈf(15)ɫt",
  vault: "ˈv(15)ɫt",
  // US would put it in LOT or NORTH, but should be CLOTH (16)
  floss: "ˈfɫ(16)s",
  fosse: "ˈf(16)s",
  tongs: "ˈt(16)ŋz",
  borrow: "ˈb(16)ˌɹ( 2)",
  morrow: "ˈm(16)ɹ( 2)",
  sorrow: "ˈs(16)ɹ( 2)",
  tomorrow: "təˈm(16)ˌɹ( 2)",
  sorry: "ˈs(16)ɹ(21)",
  // US pronounces as [ɔ], but should be CLOTH (16)
  categorical: "ˌk(17)təˈɡ(16)ɹ(22)kəɫ",
  florida: "ˈfɫ(16)ɹədə",
  florist: "ˈfɫ(16)ɹ(22)st",
  laurence: "ˈɫ(16)ɹəns",
  lawrence: "ˈɫ(16)ɹəns",
  laurel: "ˈɫ(16)ɹəɫ",
  laureate: "ˈɫ(16)ɹiət",
  moribund: "ˈm(16)ɹəbənd",
  // UK looks like TRAP, but should be BATH (18)
  alto: "ˈ(18)ɫt( 2)",
  basque: "ˈb(18)sk",
  masculine: "ˈm(18)skjəɫən",
  substantial: "səbˈst(18)nʃəɫ",
  transit: "ˈtɹ(18)nz(22)t",
  transport: "ˈtɹ(18)nsp( 8)t",
  transfer: "ˈtɹ(18)nsf(11)",
  transform: "ˈtɹ(18)nsf( 8)m",
  transitory: "ˈtɹ(18)nzəˌtɔɹ(21)",
  transient: "ˈtɹ(18)nziənt",
  transept: "ˈtɹ(18)ns(19)pt",
  // various that should be BATH (18)
  contralto: "kənˈtɹ(18)ɫt( 2)",
  corral: "kɝˈ(18)ɫ",
  giraffe: "d͡ʒɝˈ(18)f",
  iran: "ˌɪˈɹ(18)n",
  iraq: "ˌɪˈɹ(18)k",
  morale: "mɝˈ(18)ɫ",
  // US & UK don't distinguish ( 8) & ( 9) (NORTH & FORCE)
  force: "ˈf( 9)s",
  four: "ˈf( 9)", fours: "ˈf( 9)z", fourth: "ˈf( 9)θ", fourths: "ˈf( 9)θs",
  fourteen: "ˈf( 9)ˈt(20)n", fourteens: "ˈf( 9)ˈt(20)nz", fourteenth: "ˈf( 9)ˈt(20)nθ", fourteenths: "ˈf( 9)ˈt(20)nθs",
  forty: "ˈf( 9)ti", forties: "ˈf( 9)tiz", fortieth: "ˈf( 9)tiəθ", fortieths: "ˈf( 9)tiəθs",
  wore: "ˈw( 9)",
  sport: "ˈsp( 9)t",
  porch: "ˈp( 9)t͡ʃ",
  borne: "ˈb( 9)n",
  story: "ˈst( 9)i",
  adore: "əˈd( 9)",
  afore: "əˈf( 9)",
  before: "ˌbiˈf( 9)",
  bore: "ˈb( 9)",
  chore: "ˈt͡ʃ( 9)",
  core: "ˈk( 9)",
  deplore: "d(22)ˈpɫ( 9)",
  explore: "ɪksˈpɫ( 9)",
  fore: "ˈf( 9)",
  galore: "ɡəˈɫ( 9)",
  gore: "ˈɡ( 9)",
  ignore: "ˌ(22)ɡˈn( 9)",
  implore: "ˌ(22)mˈpɫ( 9)",
  more: "ˈm( 9)",
  ore: "ˈ( 9)",
  pore: "ˈp( 9)",
  restore: "ɹ(22)ˈst( 9)",
  score: "ˈsk( 9)",
  shore: "ˈʃ( 9)",
  snore: "ˈsn( 9)",
  sore: "ˈs( 9)",
  spore: "ˈsp( 9)",
  store: "ˈst( 9)",
  swore: "ˈsw( 9)",
  tore: "ˈt( 9)",
  whore: "ˈh( 9)",
  yore: "ˈj( 9)",
  boar: "ˈb( 9)",
  hoar: "ˈh( 9)",
  oar: "ˈ( 9)",
  roar: "ˈɹ( 9)",
  soar: "ˈs( 9)",
  floor: "ˈfɫ( 9)",
  door: "ˈd( 9)",
  pour: "ˈp( 9)",
  deport: "d(22)ˈp( 9)t",
  export: "ˈ(19)ksp( 9)t",
  fort: "ˈf( 9)t",
  import: "ˈ(22)mˌp( 9)t",
  port: "ˈp( 9)t",
  report: "ɹ(20)ˈp( 9)t",
  support: "səˈp( 9)t",
  pork: "ˈp( 9)k",
  forth: "ˈf( 9)θ",
  divorce: "d(22)ˈv( 9)s",
  afford: "əˈf( 9)d",
  ford: "ˈf( 9)d",
  horde: "ˈh( 9)d",
  sword: "ˈs( 9)d",
  forge: "ˈf( 9)d͡ʒ",
  sworn: "ˈsw( 9)n",
  torn: "ˈt( 9)n",
  worn: "ˈw( 9)n",
  portent: "ˈp( 9)tɛnt",
  porter: "ˈp( 9)t(11)",
  portrait: "ˈp( 9)tɹət",
  proportion: "pɹəˈp( 9)ʃən",
  borneo: "ˈb( 9)n(20)ˌ( 2)",
  coarse: "ˈk( 9)s",
  hoarse: "ˈh( 9)s",
  board: "ˈb( 9)d",
  hoard: "ˈh( 9)d",
  boarder: "ˈb( 9)d(11)",
  court: "ˈk( 9)t",
  course: "ˈk( 9)s",
  resource: "ˈɹis( 9)s",
  source: "ˈs( 9)s",
  mourn: "ˈm( 9)n",
  courtier: "ˈk( 9)ti(11)",
  mourning: "ˈm( 9)n(22)ŋ",
  oral: "ˈ( 9)əɫ",
  adorable: "əˈd( 9)əbəɫ",
  angora: "(17)ŋˈɡ( 9)ə",
  aurora: "ɝˈ( 9)ə",
  borax: "ˈb( 9)ˌ(17)ks",
  boron: "ˈb( 9)ˌ(14)n",
  choral: "ˈk( 9)(25)ɫ",
  dora: "ˈd( 9)(25)",
  fedora: "f(22)ˈd( 9)ə",
  flora: "ˈfɫ( 9)ə",
  floral: "ˈfɫ( 9)əɫ",
  glory: "ˈɡɫ( 9)i",
  gory: "ˈɡ( 9)i",
  moron: "ˈm( 9)ˌ(14)n",
  nora: "ˈn( 9)(25)",
  porous: "ˈp( 9)əs",
  thorax: "ˈθ( 9)æks",
  torus: "ˈt( 9)əs",
  tory: "ˈt( 9)(20)",
  censorious: "s(19)nˈs( 9)iəs",
  euphoria: "j(23)ˈf( 9)iə",
  gloria: "ˈɡɫ( 9)(20)(25)",
  glorious: "ˈɡɫ( 9)iəs",
  gregorian: "ɡɹ(25)ˈɡ( 9)(20)(25)n",
  historian: "h(22)ˈst( 9)iən",
  laborious: "ɫəˈb( 9)iəs",
  memorial: "məˈm( 9)iəɫ",
  meritorious: "ˌm(19)ɹəˈt( 9)iəs",
  moratorium: "ˌm( 9)əˈtɔɹiəm",
  notorious: "n( 2)ˈt( 9)iəs",
  pictorial: "p(22)kˈt( 9)iəɫ",
  pretorian: "pɹ(22)ˈt( 9)(20)(25)n",
  stentorian: "st(19)nˈt( 9)iən",
  thorium: "ˈθ( 9)iəm",
  victoria: "v(22)kˈt( 9)iə",
  victorian: "v(22)kˈt( 9)(20)(25)n",
  hoary: "ˈh( 9)i",
  uproarious: "(25)pˈɹ( 9)iəs",
};

/*
Noticing the difference between the UK & US phonemes gives us all sets
  except 0, 1, 3, 4 (diphthongs), 17 (æ), 19(e) 21 (i), 22 (ɪ), & 24(ʊ),
  and most notably 8/9, where we have nothing unique at all

 #  UK  US   name    samples
 ---------------------------
 0  e‍ɪ  eɪ   FACE    tape cake raid veil steak day
 1  a‍ɪ  aɪ   PRICE   ripe write arrive high try buy
 2  ə‍ʊ  oʊ   GOAT    soap joke home know so roll
 3  ɔ‍ɪ  ɔɪ   CHOICE  adroit noise join toy royal
 4  a‍ʊ  aʊ   MOUTH   out house loud count crowd cow
 5  ɑː  ɑɹ   START   far sharp bark carve farm heart
 6  e‍ə  ɛɹ   SQUARE  care fair pear where scarce vary
 7  i‍ə  ɪɹ   NEAR    beer sincere fear beard serum
 8  ɔː  ɔɹ   NORTH   for war short scorch born warm
 9  ɔː  ɔɹ   FORCE   four wore sport porch borne story
10  ɔː  ʊɹ   CURE    poor tourist pure plural jury
11  ɐ   ɝ    lettER  paper metre calendar stupor succo(u)r martyr figure
12  ɜː  ɝ    NURSE   hurt lurk urge burst jerk term
13  ɑː  ɑ    PALM    psalm father bra spa lager
14  ɒ   ɑ    LOT     stop sock dodge romp possible quality
15  ɔː  ɔ    THOUGHT taught sauce hawk jaw broad
16  ɒ   ɔ    CLOTH   cough broth cross long Boston
17  æ   æ    TRAP    tap back badge scalp hand cancel
18  ɑː  æ    BATH    staff brass ask dance sample calf
19  ɛ   ɛ    DRESS   step neck edge shelf friend ready
20  iː  i    FLEECE  creep speak leave feel key people
21  i   i    happY   copy scampi taxi sortie committee hockey Chelsea
22  ɪ   ɪ    KIT     ship sick bridge milk myth busy
23  uː  u    GOOSE   loop shoot tomb mute huge view
24  ʊ   ʊ    FOOT    put bush full good look wolf
25  ʌ   ə    STRUT   cup suck budge pulse trunk blood
26  ɐ   ə    commA   catalpa quota vodka
*/
